import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngxs/store';
import { AccountState } from 'app/state/account/state';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { SheetTemplate } from './sheet-templates.types';

@Injectable({
  providedIn: 'root',
})
export class SheetTemplatesService {
  private _sheetTemplate: BehaviorSubject<SheetTemplate | null>;
  private _sheetTemplates: BehaviorSubject<SheetTemplate[] | null>;
  private _accountId: string;

  constructor(
    private _httpClient: HttpClient,
    private store: Store,
  ) {
    this._sheetTemplate = new BehaviorSubject(null);
    this._sheetTemplates = new BehaviorSubject(null);
    this._accountId = this.store.selectSnapshot(AccountState.getAccount).id;
  }

  get sheetTemplate$(): Observable<SheetTemplate> {
    return this._sheetTemplate.asObservable();
  }

  get sheetTemplates$(): Observable<SheetTemplate[]> {
    return this._sheetTemplates.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  getSheetTemplates(): Observable<SheetTemplate[] | null> {
    return this._httpClient.get<any>(`sheet_templates`).pipe(
      map(({ sheet_templates }) => {
        this._sheetTemplates.next(sheet_templates);

        return sheet_templates;
      }),
    );
  }

  getSheetTemplateById(id: number): Observable<SheetTemplate> {
    return this._httpClient.get<any>(`sheet_templates/${id}`).pipe(
      map(({ sheet_template }) => {
        this._sheetTemplate.next(sheet_template);
        return sheet_template;
      }),
    );
  }

  createSheetTemplate(sheetTemplate: SheetTemplate): Observable<SheetTemplate> {
    return this.sheetTemplates$.pipe(
      take(1),
      switchMap((sheetTemplates) =>
        this._httpClient.post<any>(`sheet_templates`, { sheet_template: sheetTemplate }).pipe(
          map(({ sheet_template }) => {
            this._sheetTemplates.next([sheet_template, ...sheetTemplates]);

            return sheet_template;
          }),
        ),
      ),
      catchError((error: HttpErrorResponse) => throwError(() => new Error(error.error))),
    );
  }

  updateSheetTemplate(sheetTemplate: SheetTemplate): Observable<SheetTemplate> {
    return this.sheetTemplates$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .patch<any>(`sheet_templates/${sheetTemplate.id}`, {
            sheet_template: sheetTemplate,
          })
          .pipe(
            map(({ sheet_template }) => {
              this._sheetTemplate.next(sheet_template);

              return sheet_template;
            }),
          ),
      ),
    );
  }

  deleteSheetTemplate(id: number): Observable<SheetTemplate> {
    return this.sheetTemplates$.pipe(
      take(1),
      switchMap((sheetTemplates) =>
        this._httpClient.delete<any>(`sheet_templates/${id}`).pipe(
          map(({ sheet_template }) => {
            const index = sheetTemplates.findIndex((item) => item.id === id);

            sheetTemplates.splice(index, 1);

            this._sheetTemplates.next(sheetTemplates);

            return sheet_template;
          }),
        ),
      ),
    );
  }

  uploadDocument(sheetTemplate: SheetTemplate, document: File): Observable<SheetTemplate> {
    const uploadData = new FormData();
    uploadData.append('document[attachment]', document);

    return this.sheetTemplates$.pipe(
      take(1),
      switchMap((sheetTemplates) =>
        this._httpClient.post<any>(`sheet_templates/${sheetTemplate.id}/upload_document`, uploadData).pipe(
          map(({ sheet_template }) => {
            let updatedSheetTemplate = sheet_template;

            const index = sheetTemplates.findIndex((item) => item.id === updatedSheetTemplate.id);

            sheetTemplates[index] = updatedSheetTemplate;

            this._sheetTemplates.next(sheetTemplates);
            this._sheetTemplate.next(updatedSheetTemplate);

            return updatedSheetTemplate;
          }),
        ),
      ),
    );
  }
}
